import { SingleTemplateModel } from 'templates/types';
import { API_URL } from 'utils/endpoints';
import { OtherTemplatesResponseModel } from './types';
import { TEMPLATES_REQUEST_LIMIT } from '../pages/Categories/constants';

const extractIdAndName = (id: string): [string, string] => {
  return id.includes('-')
    ? [decodeURIComponent(id.split('-').slice(0, -1).join(' ')), id.split('-').pop() || '']
    : ['', id];
};

const validateTemplateId = (templateId: string): void => {
  if (!templateId || Number.isNaN(Number(templateId))) {
    throw new Error('Invalid template ID.');
  }
};

const fetchRelatedTemplates = async (
  templateData: SingleTemplateModel,
  headers?: any,
): Promise<OtherTemplatesResponseModel> => {
  const relatedTemplatesUrl = `${API_URL}/templates/discover/ratio?ratio=${templateData.subtypes[0]}&market=website&limit=${TEMPLATES_REQUEST_LIMIT}`;
  const relatedResponse = await fetch(relatedTemplatesUrl, {
    headers,
  });

  const relatedTemplatesData = await relatedResponse.json();

  const otherTemplates =
    relatedTemplatesData.response
      ?.map(template => ({
        ...template,
        url: template.preview_url,
      }))
      ?.filter(item => item.id !== templateData.id.toString()) || [];

  return {
    response: otherTemplates,
    metadata: {
      next_page: relatedTemplatesData.metadata.next_page,
    },
  };
};

const fetchTemplateData = async (
  templateId: string,
  headers: any,
): Promise<SingleTemplateModel> => {
  const url = `${API_URL}/v1/content/items/${templateId}`;
  const response = await fetch(url, {
    headers,
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch template data. Status: ${response.status}`);
  }

  return response.json();
};

const validateDisplayName = (
  displayNameEncoded: string,
  templateData: SingleTemplateModel,
): void => {
  const urlDisplayName = templateData?.display_name?.toLowerCase().split('-').join(' ');

  if (displayNameEncoded && urlDisplayName !== displayNameEncoded) {
    throw new Error('Display names do not match.');
  }
};

const getSingleTemplate = async (
  id: string,
  headers?: any,
): Promise<{
  templateData: SingleTemplateModel;
  otherTemplates: OtherTemplatesResponseModel;
}> => {
  try {
    const [displayNameEncoded, templateId] = extractIdAndName(id);

    validateTemplateId(templateId);

    const templateData = await fetchTemplateData(templateId, headers);
    validateDisplayName(displayNameEncoded, templateData);

    const otherTemplates = await fetchRelatedTemplates(templateData, headers);

    return {
      templateData,
      otherTemplates,
    };
  } catch (error) {
    throw new Error(`Error fetching template: ${error.message}`);
  }
};

export default getSingleTemplate;
